<template>
  <div class="register-page">
    <!-- 动态背景 -->
    <div class="bg-decoration">
      <div class="bg-orb bg-orb--1"></div>
      <div class="bg-orb bg-orb--2"></div>
      <div class="bg-orb bg-orb--3"></div>
      <div class="bg-grid"></div>
    </div>

    <div class="register-card">
      <!-- 品牌区 -->
      <div class="brand-section">
        <div class="brand-logo">
          <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" class="brand-icon">
            <circle cx="16" cy="16" r="14" fill="url(#reg-heart)"/>
            <path d="M16 10C13.5 7.5 9.5 7.5 7.5 10C5.5 12.5 5.5 16 7.5 18L16 26L24.5 18C26.5 16 26.5 12.5 24.5 10C22.5 7.5 18.5 7.5 16 10Z" fill="white" fill-opacity="0.95"/>
            <defs>
              <linearGradient id="reg-heart" x1="0" y1="0" x2="32" y2="32">
                <stop stop-color="#8B7CF7"/>
                <stop offset="1" stop-color="#A99BFF"/>
              </linearGradient>
            </defs>
          </svg>
        </div>
        <h1 class="brand-title">创建账号</h1>
        <p class="brand-subtitle">开启你的缘分之旅</p>
      </div>

      <!-- 错误提示 -->
      <transition name="slide-down">
        <div v-if="error" class="error-tip">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {{ error }}
        </div>
      </transition>

      <!-- 注册表单 -->
      <form @submit.prevent="handleRegister" class="register-form">
        <div class="form-field">
          <div class="field-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <input type="text" v-model="form.username" placeholder="请设置用户名（4-20位）" class="field-input">
        </div>

        <div class="form-field">
          <div class="field-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </div>
          <input type="password" v-model="form.password" placeholder="请设置密码（6-20位）" class="field-input">
        </div>

        <div class="form-field code-field">
          <div class="field-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
          </div>
          <input type="tel" v-model="form.phone" placeholder="请输入手机号" class="field-input">
          <button type="button" class="code-btn" :disabled="sending || !form.phone" @click="sendVerifyCode">
            {{ sending ? `${countdown}s` : '获取验证码' }}
          </button>
        </div>

        <div class="form-field">
          <div class="field-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <input type="text" v-model="form.verifyCode" placeholder="请输入验证码" class="field-input">
        </div>

        <label class="agree-item">
          <input type="checkbox" id="agree" v-model="isAgree" class="agree-checkbox">
          <span class="agree-checkmark"></span>
          <span class="agree-text">我已阅读并同意《服务协议》《隐私政策》</span>
        </label>

        <button type="submit" :disabled="loading || !isAgree" class="submit-btn">
          <span v-if="loading" class="btn-loading">
            <span class="spinner"></span> 注册中...
          </span>
          <span v-else>立即注册</span>
        </button>
      </form>

      <!-- 页脚 -->
      <div class="register-footer">
        <span class="footer-text">已有账号？</span>
        <router-link to="/login" class="footer-link">立即登录</router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import authApi from '../api/auth'
const router = useRouter()
const form = ref({ username: '', password: '', phone: '', verifyCode: '', codeId: '' })
const loading = ref(false)
const sending = ref(false)
const countdown = ref(60)
const error = ref('')
const isAgree = ref(false)
let countdownTimer = null

const sendVerifyCode = async () => {
  if (!/^1[3-9]\d{9}$/.test(form.value.phone)) { error.value = '手机号格式错误'; return }
  try {
    sending.value = true
    const res = await authApi.sendRegisterSms(router.app.config.globalProperties.axios, form.value.phone)
    form.value.codeId = res.data.data
    error.value = ''
    countdownTimer = setInterval(() => {
      countdown.value--
      if (countdown.value <= 0) { clearInterval(countdownTimer); sending.value = false; countdown.value = 60 }
    }, 1000)
  } catch (err) { error.value = '发送失败'; sending.value = false }
}

const handleRegister = async () => {
  if (!form.value.username || form.value.username.length < 4) { error.value = '用户名4-20位'; return }
  if (!form.value.password || form.value.password.length < 6) { error.value = '密码6-20位'; return }
  if (!/^1[3-9]\d{9}$/.test(form.value.phone)) { error.value = '手机号错误'; return }
  if (!form.value.verifyCode) { error.value = '请输入验证码'; return }
  if (!isAgree.value) { error.value = '请同意协议'; return }
  loading.value = true
  error.value = ''
  try {
    await authApi.register(router.app.config.globalProperties.axios, form.value)
    alert('注册成功！请登录')
    router.push('/login')
  } catch (err) { error.value = '注册失败' }
  finally { loading.value = false }
}

onUnmounted(() => { if (countdownTimer) clearInterval(countdownTimer) })
</script>

<style scoped>
.register-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--app-bg-page);
  position: relative;
  overflow: hidden;
  padding: 20px;
}

/* ── 动态背景 ── */
.bg-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(139, 124, 247, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(139, 124, 247, 0.03) 1px, transparent 1px);
  background-size: 48px 48px;
}

.bg-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
}

.bg-orb--1 {
  width: 400px;
  height: 400px;
  background: rgba(139, 124, 247, 0.12);
  top: -120px;
  left: -80px;
  animation: orbFloat 12s ease-in-out infinite;
}

.bg-orb--2 {
  width: 320px;
  height: 320px;
  background: rgba(255, 126, 126, 0.10);
  bottom: -80px;
  right: -60px;
  animation: orbFloat 16s ease-in-out infinite reverse;
}

.bg-orb--3 {
  width: 180px;
  height: 180px;
  background: rgba(251, 191, 36, 0.08);
  top: 40%;
  right: 10%;
  animation: orbFloat 14s ease-in-out infinite 3s;
}

@keyframes orbFloat {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.05); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

/* ── 注册卡片 ── */
.register-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 440px;
  background: var(--app-bg-glass);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: var(--app-shadow-glass), 0 0 0 1px rgba(139, 124, 247, 0.06);
  padding: 40px 36px 32px;
}

/* ── 品牌区 ── */
.brand-section {
  text-align: center;
  margin-bottom: 28px;
}

.brand-logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.brand-icon {
  width: 54px;
  height: 54px;
  filter: drop-shadow(0 4px 12px rgba(139, 124, 247, 0.25));
}

.brand-title {
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #8B7CF7, #A99BFF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 4px;
  letter-spacing: 0.02em;
}

.brand-subtitle {
  font-size: 0.875rem;
  color: var(--app-text-secondary);
}

/* ── 错误提示 ── */
.slide-down-enter-active {
  transition: all 0.3s ease;
}
.slide-down-leave-active {
  transition: all 0.2s ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.error-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 14px;
  margin-bottom: 16px;
  border-radius: 10px;
  background: #FEF2F2;
  color: #DC2626;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid #FECACA;
}

/* ── 表单 ── */
.register-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-field {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  height: 50px;
  border-radius: 14px;
  border: 2px solid var(--app-border-light);
  background: var(--app-bg-soft);
  transition: all 0.25s ease;
}

.form-field:focus-within {
  border-color: #8B7CF7;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(139, 124, 247, 0.08);
}

.field-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: var(--app-text-muted);
  transition: color 0.25s ease;
}

.form-field:focus-within .field-icon {
  color: #8B7CF7;
}

.field-input {
  flex: 1;
  height: 100%;
  border: none;
  background: transparent;
  font-size: 0.9375rem;
  color: var(--app-text-primary);
  outline: none;
  font-family: inherit;
}

.field-input::placeholder {
  color: var(--app-text-muted);
}

/* ── 验证码特殊 ── */
.code-field {
  padding-right: 4px;
}

.code-btn {
  flex-shrink: 0;
  height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: 10px;
  background: var(--app-gradient-accent);
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  font-family: inherit;
}

.code-btn:hover:not(:disabled) {
  box-shadow: 0 4px 14px rgba(139, 124, 247, 0.3);
}

.code-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── 协议勾选 ── */
.agree-item {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 4px 0;
  user-select: none;
}

.agree-checkbox {
  width: 18px;
  height: 18px;
  accent-color: #8B7CF7;
  cursor: pointer;
}

.agree-text {
  font-size: 0.8125rem;
  color: var(--app-text-secondary);
  line-height: 1.4;
}

/* ── 提交按钮 ── */
.submit-btn {
  height: 50px;
  border: none;
  border-radius: 14px;
  background: var(--app-gradient-accent);
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  margin-top: 2px;
  letter-spacing: 0.02em;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(139, 124, 247, 0.35);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn-loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── 页脚 ── */
.register-footer {
  text-align: center;
  margin-top: 24px;
  font-size: 0.875rem;
}

.footer-text {
  color: var(--app-text-secondary);
}

.footer-link {
  color: #8B7CF7;
  text-decoration: none;
  font-weight: 600;
  margin-left: 4px;
  transition: color 0.2s;
}

.footer-link:hover {
  color: var(--app-color-primary);
}

/* ── 响应式 ── */
@media (max-width: 480px) {
  .register-card {
    padding: 32px 24px 28px;
    border-radius: 20px;
  }

  .brand-icon {
    width: 46px;
    height: 46px;
  }

  .brand-title {
    font-size: 1.25rem;
  }

  .form-field {
    height: 46px;
    padding: 0 14px;
  }

  .code-btn {
    height: 34px;
    padding: 0 10px;
    font-size: 0.75rem;
  }

  .submit-btn {
    height: 46px;
  }
}
</style>
